import React from 'react';
import { Flex, Box, Heading, Text, Card, CardHeader, CardContent, DataTable, LoadingOrbit, Button } from '@chatsdet/ui';
import { Clock } from '@phosphor-icons/react';
import { useScheduledJobs } from './useScheduledJobs.js';
import { getScheduleColumns } from './columns.js';
import { CreateScheduleDialog } from './CreateScheduleDialog.js';
import { EditScheduleDialog } from './EditScheduleDialog.js';
import { ScheduleRunHistory } from './ScheduleRunHistory.js';
import type { Schedule } from './types.js';

export function ScheduledJobsTab() {
  const [selectedScheduleId, setSelectedScheduleId] = React.useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [editingSchedule, setEditingSchedule] = React.useState<Schedule | null>(null);
  const [showEnabledOnly, setShowEnabledOnly] = React.useState(true);

  const {
    schedulesQuery,
    jobDefinitionsQuery,
    scheduleRunsQuery,
    createMutation,
    deleteMutation,
    runNowMutation,
    updateMutation,
  } = useScheduledJobs(selectedScheduleId);

  const handleDelete = React.useCallback(
    (scheduleId: string) => {
      if (window.confirm('Are you sure you want to delete this schedule?')) {
        deleteMutation.mutate(scheduleId, {
          onSuccess: () => setSelectedScheduleId((prev) => (prev === scheduleId ? null : prev)),
        });
      }
    },
    [deleteMutation]
  );

  const handleRunNow = React.useCallback(
    (scheduleId: string) => {
      if (window.confirm('Run this schedule immediately?')) {
        runNowMutation.mutate(scheduleId);
      }
    },
    [runNowMutation]
  );

  const handleOpenEdit = React.useCallback((schedule: Schedule) => {
    setEditingSchedule(schedule);
    setIsEditDialogOpen(true);
  }, []);

  const scheduleColumns = React.useMemo(
    () =>
      getScheduleColumns({
        onRunNow: handleRunNow,
        onEdit: handleOpenEdit,
        onDelete: handleDelete,
        isRunning: runNowMutation.isPending,
        isUpdating: updateMutation.isPending,
        isDeleting: deleteMutation.isPending,
      }),
    [handleRunNow, handleOpenEdit, handleDelete, runNowMutation.isPending, updateMutation.isPending, deleteMutation.isPending]
  );

  const allSchedules = schedulesQuery.data?.schedules;

  const visibleSchedules = React.useMemo(
    () => (showEnabledOnly ? (allSchedules ?? []).filter((s) => s.enabled) : (allSchedules ?? [])),
    [allSchedules, showEnabledOnly]
  );

  return (
    <Box className="space-y-6">
      <Card padding="lg">
        <CardHeader>
          <Flex align="start" gap="md">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Clock size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <Heading level={3}>Scheduled Jobs</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Manage recurring and one-time scheduled jobs
              </Text>
            </div>
            <CreateScheduleDialog
              isOpen={isCreateDialogOpen}
              onOpenChange={setIsCreateDialogOpen}
              createMutation={createMutation}
              jobDefinitions={jobDefinitionsQuery.data?.definitions ?? []}
              isLoadingDefinitions={jobDefinitionsQuery.isLoading}
            />
          </Flex>
        </CardHeader>
        <CardContent>
          {schedulesQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-10">
              <LoadingOrbit size="lg" />
            </Flex>
          ) : schedulesQuery.isError ? (
            <Text className="text-destructive">
              {schedulesQuery.error instanceof Error
                ? schedulesQuery.error.message
                : 'Failed to load schedules'}
            </Text>
          ) : allSchedules?.length ? (
            <DataTable
              columns={scheduleColumns}
              data={visibleSchedules}
              onRowClick={(schedule) => setSelectedScheduleId(schedule.id)}
              searchable
              searchPlaceholder="Filter schedules..."
              preservePageOnDataChange
              toolbarContent={
                <Button
                  variant={showEnabledOnly ? 'primary' : 'outline'}
                  size="sm"
                  onPress={() => setShowEnabledOnly((prev) => !prev)}
                >
                  {showEnabledOnly ? 'Enabled only' : 'All schedules'}
                </Button>
              }
            />
          ) : (
            <div className="text-center py-12">
              <Clock size={48} className="mx-auto text-muted-foreground mb-4" />
              <Text className="text-muted-foreground">No scheduled jobs yet</Text>
              <Text size="sm" className="text-muted-foreground mt-2">
                Create a schedule to automate job execution
              </Text>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedScheduleId && (
        <ScheduleRunHistory
          runsQuery={scheduleRunsQuery}
          onClose={() => setSelectedScheduleId(null)}
        />
      )}

      <EditScheduleDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        schedule={editingSchedule}
        onScheduleCleared={() => setEditingSchedule(null)}
        updateMutation={updateMutation}
      />
    </Box>
  );
}
